<template>
  <div class="db">
    <sidebar
      :active="pag"
      :collapsed="collapsed"
      :open="menuMovil"
      @pag="pagina"
      @toggle="toggleCollapsed"
      @close="menuMovil = false"
    />

    <transition name="db-fade">
      <div v-if="menuMovil" class="db-overlay db-only-mobile" @click="menuMovil = false"></div>
    </transition>

    <div class="db-main">
      <header class="db-topbar">
        <button type="button" class="db-icon-btn db-only-mobile" aria-label="Abrir menú" @click="menuMovil = true">
          <span class="material-symbols-outlined">menu</span>
        </button>

        <h1 class="db-topbar-title">{{ tituloSeccion }}</h1>

        <div class="db-topbar-actions">
          <router-link :to="{ name: 'inicio' }" target="_blank" class="db-btn db-btn--ghost db-btn--sm db-only-desktop">
            <span class="material-symbols-outlined">open_in_new</span> Ver web
          </router-link>
          <button
            type="button"
            class="db-icon-btn db-icon-btn--bordered"
            :aria-label="$store.getters.isDarkMode ? 'Activar modo claro' : 'Activar modo oscuro'"
            :title="$store.getters.isDarkMode ? 'Modo claro' : 'Modo oscuro'"
            @click="cambiarTema"
          >
            <span class="material-symbols-outlined">{{ $store.getters.isDarkMode ? "light_mode" : "dark_mode" }}</span>
          </button>
          <button v-if="usuario" type="button" class="db-user" :title="`${usuario.email} · Mi perfil`" @click="pagina('perfil')">
            <span class="db-user-avatar">{{ iniciales }}</span>
            <span class="db-user-info db-only-desktop">
              <span class="db-user-name">{{ usuario.name }}</span>
              <span class="db-user-role">{{ usuario.rol_etiqueta }}</span>
            </span>
          </button>
        </div>
      </header>

      <main class="db-content">
        <div v-if="cargandoUsuario" class="db-empty">
          <span class="db-spinner"></span>
          <p>Cargando panel…</p>
        </div>
        <div v-else-if="!usuario" class="db-empty">
          <span class="material-symbols-outlined">cloud_off</span>
          <p>No se pudo cargar tu usuario.</p>
          <button type="button" class="db-btn db-btn--ghost db-btn--sm" @click="infoUser">Reintentar</button>
        </div>
        <component :is="view" v-else @navigate="pagina" />
      </main>
    </div>

    <modal-cambio-password-obligatorio
      :visible="Boolean(usuario && usuario.debe_cambiar_password)"
      @cambiada="alCambiarPassword"
      @salir="salir"
    />
  </div>
</template>

<script>
import sidebar from "@/components/dashboard/Sidebar.vue";
import ModalCambioPasswordObligatorio from "@/components/dashboard/modals/ModalCambioPasswordObligatorio.vue";
import panelNoticias from "@/views/dashboard/VistaEditorNoticias.vue";
import panelNoticiasCaritas from "@/views/dashboard/VistaEditorNoticiasCaritas.vue";
import createGallery from "@/views/dashboard/EditorGalerias.vue";
import popUpControlEdit from "@/views/dashboard/PopUpControlEdit.vue";
import editorHorarios from "@/views/dashboard/EditorHorarios.vue";
import vistaGestorUsuarios from "@/views/dashboard/VistaGestorUsuarios.vue";
import vistaRoles from "@/views/dashboard/VistaRoles.vue";
import vistaPerfil from "@/views/dashboard/VistaPerfil.vue";
import resumenDashboard from "@/views/dashboard/ResumenDashboard.vue";
import vistaURLInteres from "@/views/dashboard/VistaURLInteres.vue";
import vistaAvisosMovil from "@/views/dashboard/VistaAvisosMovil.vue";
import axios from "@/directives/axios.js";
import { toggleTheme } from "@/directives/theme.js";
import { avisar, iniciales, avisarError } from "@/directives/dbDialogs.js";
import { SECCIONES, seccionesPermitidas } from "@/components/dashboard/secciones.js";

const VISTAS = {
  principal: resumenDashboard,
  noticias: panelNoticias,
  noticiasCaritas: panelNoticiasCaritas,
  galeria: createGallery,
  urls: vistaURLInteres,
  popUp: popUpControlEdit,
  avisosMovil: vistaAvisosMovil,
  horarios: editorHorarios,
  usuarios: vistaGestorUsuarios,
  roles: vistaRoles,
  perfil: vistaPerfil,
};

export default {
  name: "DashboardView",

  components: {
    sidebar,
    ModalCambioPasswordObligatorio,
  },

  metaInfo: {
    title: "Panel de control",
  },

  data() {
    let collapsed = false;
    try {
      collapsed = localStorage.getItem("db-sidebar") === "collapsed";
    } catch (e) {
      collapsed = false;
    }
    return {
      collapsed,
      menuMovil: false,
      cargandoUsuario: true,
      ultimoRefresco: 0,
      pag: "principal",
    };
  },

  computed: {
    usuario() {
      return this.$store.getters.usuario;
    },

    permitidas() {
      return seccionesPermitidas(this.$store.getters.puede).map((s) => s.key);
    },

    tituloSeccion() {
      const seccion = SECCIONES.find((s) => s.key === this.pag);
      return seccion ? seccion.texto : "Panel de control";
    },

    iniciales() {
      return iniciales(this.usuario && (this.usuario.name || this.usuario.email));
    },

    // Una sección sin permiso nunca se pinta (el backend además la rechazaría).
    view() {
      return this.permitidas.includes(this.pag) ? VISTAS[this.pag] : resumenDashboard;
    },
  },

  watch: {
    // Si cambian los permisos (p. ej. tras editar el propio rol), se sale de la sección.
    permitidas(lista) {
      if (!lista.includes(this.pag)) this.pag = "principal";
    },
  },

  methods: {
    async infoUser() {
      this.cargandoUsuario = true;
      try {
        await this.$store.dispatch("cargarUsuario");
      } catch (error) {
        if (!error.response || error.response.status !== 401) {
          avisarError(this, error, "No se pudo recuperar el usuario.");
        }
      } finally {
        this.cargandoUsuario = false;
      }
    },

    pagina(value) {
      this.pag = value;
      this.menuMovil = false;
      window.scrollTo({ top: 0 });
    },

    // Tras un 403 del servidor se vuelve a pedir el perfil (como mucho una vez cada 5 s):
    // si le han quitado permisos, el menú y la sección actual se ajustan solos.
    async refrescarUsuario() {
      const ahora = Date.now();
      if (ahora - this.ultimoRefresco < 5000) return;
      this.ultimoRefresco = ahora;
      try {
        const antes = (this.usuario && this.usuario.permisos) || [];
        const usuario = await this.$store.dispatch("cargarUsuario");
        const perdidos = antes.filter((p) => !(usuario.permisos || []).includes(p));
        if (perdidos.length) {
          avisar(this, "Tus permisos han cambiado. El menú se ha actualizado.", "warning");
        }
      } catch (e) {
        // Si la sesión ya no es válida, el interceptor redirige al login.
      }
    },

    alCambiarPassword() {
      avisar(this, "Contraseña actualizada. Ya puedes usar el panel.");
      this.$store.dispatch("initialDataChange").catch(() => {});
    },

    async salir() {
      try {
        await axios.post("/logout");
      } catch (error) {
        // El token puede haber caducado; se limpia igualmente.
      } finally {
        sessionStorage.removeItem("token_parmada");
        this.$store.commit("setUsuario", null);
        this.$router.replace({ name: "login" });
      }
    },

    toggleCollapsed() {
      this.collapsed = !this.collapsed;
      try {
        localStorage.setItem("db-sidebar", this.collapsed ? "collapsed" : "expanded");
      } catch (e) {
        // localStorage no disponible
      }
    },

    cambiarTema() {
      toggleTheme(this.$store);
    },
  },

  async mounted() {
    window.addEventListener("sd:password-requerida", this.refrescarUsuario);
    window.addEventListener("sd:sin-permiso", this.refrescarUsuario);

    await this.infoUser();
    if (this.usuario && !this.usuario.debe_cambiar_password) {
      this.$store.dispatch("initialDataChange").catch(() => {});
    }
  },

  beforeDestroy() {
    window.removeEventListener("sd:password-requerida", this.refrescarUsuario);
    window.removeEventListener("sd:sin-permiso", this.refrescarUsuario);
  },
};
</script>

<style scoped>
.db-user {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: 4px;
  padding: 2px 6px 2px 2px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.db-user:hover,
.db-user:focus-visible {
  background: var(--sd-surface-2);
}

.db-user-avatar {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--sd-primary);
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--sd-primary-contrast);
}

.db-user-info {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.db-user-name {
  max-width: 160px;
  overflow: hidden;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--sd-text);
}

.db-user-role {
  font-size: 0.75rem;
  color: var(--sd-muted);
}

.db-fade-enter-active,
.db-fade-leave-active {
  transition: opacity 0.2s;
}

.db-fade-enter,
.db-fade-leave-to {
  opacity: 0;
}
</style>
